import React, { useState, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button, Dialog, DialogTitle, DialogContent, Typography } from '@mui/material';
import { GRAPHQL_AUTH_MODE } from '@aws-amplify/api-graphql';
import { getGQLAPI } from '@mcn-platform/models';
import { indexedTextPreview } from 'graphql/queries';

/** Capitalize first letter for objectType (course -> Course). */
function toObjectType(modelName: string): string {
  if (!modelName) return '';
  return modelName.charAt(0).toUpperCase() + modelName.slice(1);
}

export type IndexedTextPreviewButtonProps = {
  modelName?: string;
  control?: unknown;
};

const IndexedTextPreviewButton = ({ modelName }: IndexedTextPreviewButtonProps) => {
  const { getValues } = useFormContext();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = useCallback(async () => {
    const values = getValues();
    const organizationId = values.organizationID ?? values.organizationId ?? values.organization?.id ?? values.organization?.key;
    const objectID = values.id;
    if (!organizationId || !objectID || !modelName) {
      setError('Missing organization or item ID');
      setOpen(true);
      return;
    }
    setOpen(true);
    setLoading(true);
    setError(null);
    setText('');
    try {
      const result: any = await getGQLAPI().graphql({
        query: indexedTextPreview,
        variables: {
          objectType: toObjectType(modelName),
          organizationID: organizationId,
          objectID,
        },
        authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
      });
      const errs = result?.errors;
      if (errs?.length) {
        setError(errs.map((e: any) => e.message).join('; '));
        return;
      }
      const preview = result?.data?.indexedTextPreview;
      setText(preview?.text ?? '(No text)');
    } catch (err: any) {
      setError(err?.message ?? String(err));
    } finally {
      setLoading(false);
    }
  }, [getValues, modelName]);

  const handleClose = () => setOpen(false);

  return (
    <>
      <Button variant="outlined" size="small" onClick={handleClick} sx={{ mt: 1 }}>
        Indexed Text
      </Button>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Indexed Text Preview</DialogTitle>
        <DialogContent sx={{ minHeight: 200 }}>
          {loading && <Typography color="text.secondary">Loading...</Typography>}
          {error && (
            <Typography color="error" sx={{ mb: 1 }}>
              {error}
            </Typography>
          )}
          {!loading && !error && (
            <Typography
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              }}
            >
              {text || '(Empty)'}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default IndexedTextPreviewButton;
